/* ==========================================================================
   wango-case-3d — the real wango clamshell, rendered with raw WebGL.

   Ported from buildCaseModel() + ProductScrollReveal in
   lib/product-reveal.tsx. The React build used three.js
   (MeshPhysicalMaterial + RoomEnvironment). This section only ever shows ONE
   model that spins, so a ~300-line dedicated renderer replaces a 750 KB
   dependency. The geometry is identical and generated by the same formulas:

     • body   — a revolved rounded-rectangle profile (R 1.0, half-height 0.34,
                corner radius 0.17): a rounded pebble, not a squashed sphere
     • seam   — a fine dark torus groove at the equator
     • mark   — the lowercase "wango" wordmark on the lid, warm off-white,
                never mirrored (product-spec/wango-visual-lock.md)
     • clasp  — chrome cylinder + loop on the rim (wrist lanyard; the product
                is never worn on the neck)

   What is NOT identical: three's physically-based BRDF and its PMREM room
   environment. Here the body is a Lambert + hemisphere-fill + Fresnel-sheen
   shader and the chrome reflects a small analytic studio environment. Both
   were tuned so the periwinkle stays a saturated mid-tone and never washes
   pale, which is the binding rule in the visual lock.
   ========================================================================== */
(function (root) {
  "use strict";

  /* ── tiny mat4 (column-major, like GL) ─────────────────────────────────── */
  function ident() { return [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1]; }
  function mul(a, b) {                       /* a * b */
    var o = new Array(16);
    for (var c = 0; c < 4; c++)
      for (var r = 0; r < 4; r++)
        o[c*4+r] = a[r]*b[c*4] + a[4+r]*b[c*4+1] + a[8+r]*b[c*4+2] + a[12+r]*b[c*4+3];
    return o;
  }
  function persp(fovDeg, aspect, n, f) {
    var t = 1 / Math.tan(fovDeg * Math.PI / 360);
    return [t/aspect,0,0,0, 0,t,0,0, 0,0,(f+n)/(n-f),-1, 0,0,2*f*n/(n-f),0];
  }
  function rotX(a) { var c=Math.cos(a), s=Math.sin(a); return [1,0,0,0, 0,c,s,0, 0,-s,c,0, 0,0,0,1]; }
  function rotY(a) { var c=Math.cos(a), s=Math.sin(a); return [c,0,-s,0, 0,1,0,0, s,0,c,0, 0,0,0,1]; }
  function rotZ(a) { var c=Math.cos(a), s=Math.sin(a); return [c,s,0,0, -s,c,0,0, 0,0,1,0, 0,0,0,1]; }
  function scaleM(k) { return [k,0,0,0, 0,k,0,0, 0,0,k,0, 0,0,0,1]; }
  function trans(x, y, z) { return [1,0,0,0, 0,1,0,0, 0,0,1,0, x,y,z,1]; }

  /* ── geometry builders → {pos, nrm, uv, idx} ───────────────────────────── */
  function lathe(profile, segs) {
    /* Normals come from the profile tangent, not from face averaging, so the
       rounded rim shades smoothly without a faceted band. */
    var n = profile.length, pos = [], nrm = [], uv = [], idx = [];
    var pn = profile.map(function (p, i) {
      var a = profile[Math.max(0, i - 1)], b = profile[Math.min(n - 1, i + 1)];
      var tr = b[0] - a[0], ty = b[1] - a[1];
      var l = Math.hypot(tr, ty) || 1;
      return [ty / l, -tr / l];              /* outward in the profile plane */
    });
    for (var s = 0; s <= segs; s++) {
      var th = (s / segs) * Math.PI * 2, c = Math.cos(th), sn = Math.sin(th);
      for (var i = 0; i < n; i++) {
        pos.push(profile[i][0] * c, profile[i][1], profile[i][0] * sn);
        nrm.push(pn[i][0] * c, pn[i][1], pn[i][0] * sn);
        uv.push(s / segs, i / (n - 1));
      }
    }
    for (var s2 = 0; s2 < segs; s2++)
      for (var i2 = 0; i2 < n - 1; i2++) {
        var a0 = s2 * n + i2, b0 = (s2 + 1) * n + i2;
        idx.push(a0, b0, a0 + 1, b0, b0 + 1, a0 + 1);
      }
    return { pos: pos, nrm: nrm, uv: uv, idx: idx };
  }

  /* Torus around `axis` ('y' → ring in XZ, 'z' → ring in XY). */
  function torus(R, r, majorSegs, minorSegs, axis) {
    var pos = [], nrm = [], uv = [], idx = [];
    for (var i = 0; i <= majorSegs; i++) {
      var th = (i / majorSegs) * Math.PI * 2, ct = Math.cos(th), st = Math.sin(th);
      for (var j = 0; j <= minorSegs; j++) {
        var ph = (j / minorSegs) * Math.PI * 2, cp = Math.cos(ph), sp = Math.sin(ph);
        var ring = R + r * cp;
        if (axis === "y") { pos.push(ring*ct, r*sp, ring*st); nrm.push(cp*ct, sp, cp*st); }
        else              { pos.push(ring*ct, ring*st, r*sp); nrm.push(cp*ct, cp*st, sp); }
        uv.push(i / majorSegs, j / minorSegs);
      }
    }
    for (var a = 0; a < majorSegs; a++)
      for (var b = 0; b < minorSegs; b++) {
        var p0 = a * (minorSegs + 1) + b, p1 = (a + 1) * (minorSegs + 1) + b;
        idx.push(p0, p1, p0 + 1, p1, p1 + 1, p0 + 1);
      }
    return { pos: pos, nrm: nrm, uv: uv, idx: idx };
  }

  /* Cylinder along the X axis (the clasp). */
  function cylinderX(r, len, segs) {
    var pos = [], nrm = [], uv = [], idx = [], h = len / 2;
    for (var i = 0; i <= segs; i++) {
      var a = (i / segs) * Math.PI * 2, c = Math.cos(a), s = Math.sin(a);
      pos.push(-h, r*c, r*s, h, r*c, r*s);
      nrm.push(0, c, s, 0, c, s);
      uv.push(i/segs, 0, i/segs, 1);
    }
    for (var k = 0; k < segs; k++) { var q = k * 2; idx.push(q, q+1, q+2, q+1, q+3, q+2); }
    /* end caps */
    [[-h, -1], [h, 1]].forEach(function (cap) {
      var base = pos.length / 3;
      pos.push(cap[0], 0, 0); nrm.push(cap[1], 0, 0); uv.push(.5, .5);
      for (var m = 0; m <= segs; m++) {
        var aa = (m / segs) * Math.PI * 2;
        pos.push(cap[0], r*Math.cos(aa), r*Math.sin(aa)); nrm.push(cap[1], 0, 0); uv.push(.5, .5);
      }
      for (var m2 = 0; m2 < segs; m2++) {
        if (cap[1] > 0) idx.push(base, base + m2 + 1, base + m2 + 2);
        else idx.push(base, base + m2 + 2, base + m2 + 1);
      }
    });
    return { pos: pos, nrm: nrm, uv: uv, idx: idx };
  }

  /* Flat disc on the lid, facing +Y. UV maps the whole disc to the texture
     square exactly as three's CircleGeometry does, so the wordmark lands
     identically. World z = −(canvas y), hence the sign on v. */
  function disc(r, segs, y) {
    var pos = [0, y, 0], nrm = [0, 1, 0], uv = [.5, .5], idx = [];
    for (var i = 0; i <= segs; i++) {
      var a = (i / segs) * Math.PI * 2, x = r * Math.cos(a), z = r * Math.sin(a);
      pos.push(x, y, z); nrm.push(0, 1, 0); uv.push(x / (2 * r) + .5, -z / (2 * r) + .5);
    }
    for (var k = 0; k < segs; k++) idx.push(0, k + 2, k + 1);
    return { pos: pos, nrm: nrm, uv: uv, idx: idx };
  }

  function caseParts() {
    var R = 1.0, H = 0.34, rc = 0.17, seg = 8, pts = [[0, -H], [R - rc, -H]], i, a;
    for (i = 1; i <= seg; i++) { a = -Math.PI/2 + (i/seg) * (Math.PI/2); pts.push([R - rc + Math.cos(a)*rc, -H + rc + Math.sin(a)*rc]); }
    pts.push([R, H - rc]);
    for (i = 1; i <= seg; i++) { a = (i/seg) * (Math.PI/2); pts.push([R - rc + Math.cos(a)*rc, H - rc + Math.sin(a)*rc]); }
    pts.push([0, H]);
    return {
      R: R, H: H,
      body:  lathe(pts, 160),
      seam:  torus(R * 0.999, 0.006, 200, 12, "y"),
      mark:  disc(R * 0.66, 80, H + 0.001),
      clasp: cylinderX(0.055, 0.15, 24),
      loop:  torus(0.1, 0.022, 48, 14, "z")
    };
  }

  /* ── shaders ───────────────────────────────────────────────────────────── */
  var VS =
    "attribute vec3 aPos; attribute vec3 aNrm; attribute vec2 aUv;" +
    "uniform mat4 uProj; uniform mat4 uView; uniform mat4 uModel;" +
    "varying vec3 vN; varying vec3 vP; varying vec2 vUv;" +
    "void main(){" +
    "  vec4 wp = uModel * vec4(aPos, 1.0);" +
    "  vP = wp.xyz;" +
    "  vN = mat3(uModel) * aNrm;" +          /* uniform scale + rotation only */
    "  vUv = aUv;" +
    "  gl_Position = uProj * uView * wp;" +
    "}";

  var FS =
    "precision highp float;" +
    "varying vec3 vN; varying vec3 vP; varying vec2 vUv;" +
    "uniform vec3 uCam; uniform vec3 uColor; uniform vec3 uSheen;" +
    "uniform float uMode; uniform float uExposure; uniform sampler2D uTex;" +
    "vec3 lin(vec3 c){ return pow(c, vec3(2.2)); }" +
    /* a small analytic studio: cool sky, dark floor, two soft boxes */
    "vec3 studio(vec3 d){" +
    "  float up = d.y * 0.5 + 0.5;" +
    "  vec3 c = mix(vec3(0.10,0.10,0.13), vec3(0.62,0.66,0.78), smoothstep(0.0,1.0,up));" +
    "  c += vec3(1.0,0.97,0.92) * pow(max(dot(d, normalize(vec3(2.4,3.2,2.6))), 0.0), 26.0) * 1.6;" +
    "  c += vec3(0.74,0.80,1.0) * pow(max(dot(d, normalize(vec3(-2.6,1.2,-2.2))), 0.0), 18.0) * 0.9;" +
    "  return c;" +
    "}" +
    "vec3 aces(vec3 x){ return clamp((x*(2.51*x+0.03))/(x*(2.43*x+0.59)+0.14), 0.0, 1.0); }" +
    "void main(){" +
    "  vec3 N = normalize(vN);" +
    "  vec3 V = normalize(uCam - vP);" +
    "  if (!gl_FrontFacing) N = -N;" +
    "  vec3 key = normalize(vec3(2.4,3.2,2.6));" +
    "  vec3 rim = normalize(vec3(-2.6,1.2,-2.2));" +
    "  float nl = max(dot(N,key),0.0), nr = max(dot(N,rim),0.0);" +
    "  float nv = clamp(dot(N,V), 0.0, 1.0);" +
    "  vec3 col;" +
    "  if (uMode < 0.5) {" +
    /* body / seam: matte pearlescent */
    "    vec3 base = lin(uColor);" +
    "    vec3 hemi = mix(vec3(0.05,0.045,0.07), vec3(0.16,0.16,0.21), N.y*0.5+0.5);" +
    "    vec3 diff = base * (hemi + vec3(1.0,0.98,0.95)*nl*0.50 + vec3(0.74,0.79,1.0)*nr*0.18);" +
    "    float fres = pow(1.0 - nv, 3.0);" +
    "    vec3 sheen = lin(uSheen) * fres * 0.30;" +
    "    vec3 H = normalize(key + V);" +
    "    float spec = pow(max(dot(N,H),0.0), 22.0) * 0.10;" +      /* clearcoat, rough */
    "    col = diff + sheen + vec3(spec);" +
    "  } else if (uMode < 1.5) {" +
    /* chrome */
    "    vec3 r = reflect(-V, N);" +
    "    col = lin(uColor) * studio(r) * 0.95;" +
    "  } else {" +
    /* wordmark decal, alpha-blended onto the lid */
    "    vec4 t = texture2D(uTex, vUv);" +
    "    vec3 base = lin(t.rgb);" +
    "    vec3 hemi = vec3(0.30);" +
    "    col = base * (hemi + vec3(1.0)*nl*0.62);" +
    "    gl_FragColor = vec4(pow(aces(col * uExposure), vec3(1.0/2.2)), t.a);" +
    "    return;" +
    "  }" +
    "  gl_FragColor = vec4(pow(aces(col * uExposure), vec3(1.0/2.2)), 1.0);" +
    "}";

  function hex(h) {
    var n = parseInt(h.slice(1), 16);
    return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
  }

  /**
   * @param {HTMLElement} host   element the canvas is appended to (sized by CSS)
   * @returns {?{setSpin:Function, dispose:Function}}  null if WebGL is unavailable
   */
  function create(host) {
    var canvas = document.createElement("canvas");
    canvas.style.cssText = "width:100%;height:100%;display:block";
    var gl = canvas.getContext("webgl", { alpha: true, antialias: true, premultipliedAlpha: false });
    if (!gl) return null;

    function sh(type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.error("[wango-case-3d]", gl.getShaderInfoLog(s)); return null; }
      return s;
    }
    var vs = sh(gl.VERTEX_SHADER, VS), fs = sh(gl.FRAGMENT_SHADER, FS);
    if (!vs || !fs) return null;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { console.error("[wango-case-3d] link", gl.getProgramInfoLog(prog)); return null; }
    gl.useProgram(prog);
    host.appendChild(canvas);

    var parts = caseParts();
    var U = {};
    ["uProj","uView","uModel","uCam","uColor","uSheen","uMode","uExposure","uTex"].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
    var A = { pos: gl.getAttribLocation(prog, "aPos"), nrm: gl.getAttribLocation(prog, "aNrm"), uv: gl.getAttribLocation(prog, "aUv") };

    function upload(g) {
      var b = { pos: gl.createBuffer(), nrm: gl.createBuffer(), uv: gl.createBuffer(), idx: gl.createBuffer(), n: g.idx.length };
      gl.bindBuffer(gl.ARRAY_BUFFER, b.pos); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(g.pos), gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, b.nrm); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(g.nrm), gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, b.uv);  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(g.uv),  gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, b.idx);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(g.idx), gl.STATIC_DRAW);
      return b;
    }
    var mesh = {};
    ["body","seam","mark","clasp","loop"].forEach(function (k) {
      if (parts[k].pos.length / 3 > 65535) console.error("[wango-case-3d] " + k + " exceeds 16-bit indices");
      mesh[k] = upload(parts[k]);
    });

    /* wordmark texture — same 768×384 canvas, font and alpha as the original */
    var markTex = gl.createTexture();
    function paintMark() {
      var c = document.createElement("canvas"); c.width = 768; c.height = 384;
      var cx = c.getContext("2d");
      cx.fillStyle = "#f4f1ea";
      cx.font = "700 168px Quicksand, ui-rounded, 'Trebuchet MS', system-ui, sans-serif";
      cx.textAlign = "center"; cx.textBaseline = "middle"; cx.globalAlpha = 0.92;
      cx.fillText("wango", c.width / 2, c.height / 2 + 6);
      gl.bindTexture(gl.TEXTURE_2D, markTex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }
    paintMark();
    /* Quicksand may not have finished loading when this runs; repaint once it
       has, otherwise the mark falls back to a system face and stays that way. */
    if (document.fonts && document.fonts.load) {
      document.fonts.load("700 168px Quicksand").then(paintMark).catch(function () {});
    }

    var cam = [0, 0, 3.95];
    var view = trans(-cam[0], -cam[1], -cam[2]);   /* camera looks down −Z from (0,0,3.95) */
    var proj = ident();
    var spin = 0, wobbleT = 0;
    var BASE_TILT_Z = -16 * Math.PI / 180, BASE_TILT_X = -14 * Math.PI / 180;
    var COL = { body: hex("#9385d6"), seam: hex("#5b5194"), chrome: hex("#e2e5ec") };
    var SHEEN = hex("#b6abee");

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);

    function resize() {
      var w = host.clientWidth || 1, h = host.clientHeight || 1;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      proj = persp(58, w / h, 0.1, 100);
    }
    resize();
    var ro = new ResizeObserver(resize);
    ro.observe(host);

    function bind(b) {
      gl.bindBuffer(gl.ARRAY_BUFFER, b.pos); gl.enableVertexAttribArray(A.pos); gl.vertexAttribPointer(A.pos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, b.nrm); gl.enableVertexAttribArray(A.nrm); gl.vertexAttribPointer(A.nrm, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, b.uv);  gl.enableVertexAttribArray(A.uv);  gl.vertexAttribPointer(A.uv,  2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, b.idx);
    }
    function draw(b, color, mode, modelM) {
      bind(b);
      gl.uniformMatrix4fv(U.uModel, false, new Float32Array(modelM));
      gl.uniform3fv(U.uColor, color);
      gl.uniform1f(U.uMode, mode);
      gl.drawElements(gl.TRIANGLES, b.n, gl.UNSIGNED_SHORT, 0);
    }

    function render(dt) {
      wobbleT += dt;
      /* three's default Euler order is XYZ: matrix = Rx · Ry · Rz. */
      var rx = BASE_TILT_X + Math.sin(wobbleT * 0.7) * 0.022;
      var M = mul(trans(0, Math.sin(wobbleT * 0.9) * 0.014, 0),
              mul(rotX(rx), mul(rotY(spin), mul(rotZ(BASE_TILT_Z), scaleM(0.98)))));

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.useProgram(prog);
      gl.uniformMatrix4fv(U.uProj, false, new Float32Array(proj));
      gl.uniformMatrix4fv(U.uView, false, new Float32Array(view));
      gl.uniform3fv(U.uCam, cam);
      gl.uniform3fv(U.uSheen, SHEEN);
      gl.uniform1f(U.uExposure, 0.98);
      gl.disable(gl.BLEND);

      draw(mesh.body, COL.body, 0, M);
      draw(mesh.seam, COL.seam, 0, M);
      draw(mesh.clasp, COL.chrome, 1, mul(M, trans(parts.R + 0.02, 0, 0)));
      draw(mesh.loop, COL.chrome, 1, mul(M, trans(parts.R + 0.16, 0, 0)));

      /* wordmark decal: alpha-blended, no depth write, nudged toward the camera */
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.depthMask(false);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, markTex);
      gl.uniform1i(U.uTex, 0);
      draw(mesh.mark, [1, 1, 1], 2, M);
      gl.depthMask(true);
      gl.disable(gl.BLEND);
    }

    return {
      canvas: canvas,
      setSpin: function (s) { spin = s; },
      render: render,
      dispose: function () {
        ro.disconnect();
        var ext = gl.getExtension("WEBGL_lose_context");
        if (ext) ext.loseContext();
        canvas.remove();
      }
    };
  }

  root.WangoCase3D = { create: create };
})(window);
